import React, { useEffect, useState } from "react";
import { MapPin, RotateCcw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { getLocalLocation, setLocalLocation, clearLocalLocation, patchMyLocation } from "@/lib/location";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

/** Compact city chip in the customer top nav. Clicking opens a mini district
 * picker so the customer can hop districts without redoing full onboarding. */
export function CityChip() {
    const { user } = useAuth();
    const [loc, setLoc] = useState(getLocalLocation());
    const [open, setOpen] = useState(false);
    const [districts, setDistricts] = useState([]);
    const nav = useNavigate();

    useEffect(() => {
        const handler = () => setLoc(getLocalLocation());
        window.addEventListener("offernest:location", handler);
        return () => window.removeEventListener("offernest:location", handler);
    }, []);

    useEffect(() => {
        if (open && districts.length === 0) {
            api.get("/districts").then((r) => setDistricts(r.data)).catch(() => {});
        }
    }, [open, districts.length]);

    const label = loc?.district_name || (loc?.district_slug ? loc.district_slug.replace("_", " ") : "Pick district");

    const change = async (d) => {
        const value = setLocalLocation({
            ...loc,
            country_code: "IN", country_name: "India",
            state_slug: "kerala", state_name: "Kerala",
            district_slug: d.slug, district_name: d.name,
            source: "MANUAL",
        });
        if (user) {
            try {
                await patchMyLocation({
                    country_code: "IN", state_slug: "kerala",
                    district_slug: d.slug, source: "MANUAL",
                });
            } catch { /* localStorage is source of truth for anon */ }
        }
        toast.success(`Now browsing ${d.name}`);
        setOpen(false);
        nav(`/home?district_slug=${d.slug}`, { replace: true });
    };

    const resetLocation = () => {
        clearLocalLocation();
        setOpen(false);
        nav("/welcome");
    };

    return (
        <>
            <button
                onClick={() => setOpen(true)}
                data-testid="city-chip"
                className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3.5 py-1.5 font-heading text-base font-semibold capitalize text-neutral-800 transition hover:border-[var(--brand-orange)] hover:text-[var(--brand-orange)]"
            >
                <MapPin size={14} /> {label}
            </button>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-md" data-testid="city-picker">
                    <DialogHeader>
                        <DialogTitle>Change district</DialogTitle>
                    </DialogHeader>
                    <div className="grid grid-cols-2 gap-2">
                        {districts.map((d) => (
                            <button
                                key={d.slug}
                                onClick={() => change(d)}
                                data-testid={`city-pick-${d.slug}`}
                                className={`rounded-lg border px-3 py-2 text-left font-heading text-sm font-semibold transition ${loc?.district_slug === d.slug ? "border-[var(--brand-orange)] bg-[var(--brand-orange)] text-white" : "border-slate-200 hover:border-[var(--brand-orange)]"}`}
                            >
                                {d.name}
                            </button>
                        ))}
                    </div>
                    <button onClick={resetLocation} className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-neutral-500 hover:text-[var(--brand-orange)]" data-testid="city-reset">
                        <RotateCcw size={12} /> Reset location entirely
                    </button>
                </DialogContent>
            </Dialog>
        </>
    );
}

export default CityChip;
